import { createSharedComposable } from '@vueuse/core'
import axios, { type AxiosError, isAxiosError, HttpStatusCode } from 'axios'
import axiosRetry, { exponentialDelay, isIdempotentRequestError, isNetworkError, isRetryableError } from 'axios-retry'
import { storeToRefs } from 'pinia'
import { stringify } from 'qs'
import { createDeferred, type Deferred } from '@/common/deferred'
import { useAppStore } from '@/features/app/useAppStore'
import { emitToParent } from '@/features/iframe/useParentEmit'
import { type CustomDimensions } from '@/features/telemetry/types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import type { Nullable } from '@/types'

const { VITE_API_URL = 'https://electron-api.indiebi.dev' } = import.meta.env

function createApi() {
	const { trackEvent, trackException } = useTelemetry()
	const { isEmbedded } = storeToRefs(useAppStore())

	const api = axios.create({
		baseURL: VITE_API_URL,
		paramsSerializer: {
			serialize: (params) => stringify(params, { arrayFormat: 'repeat' })
		}
	})

	function isUnauthorizedRequest(error: unknown) {
		return isAxiosError(error) && error.status === HttpStatusCode.Unauthorized
	}

	function getTelemetryEventPayload(error: unknown, customDimensions?: CustomDimensions) {
		return {
			data: isAxiosError(error) ? `${error.config?.method?.toUpperCase()} ${error.config?.url}` : 'unknown',
			status: isAxiosError(error) ? String(error.status ?? '') : 'unknown',
			errorCode: isAxiosError(error) ? error.code : 'unknown',
			...customDimensions
		}
	}

	let authTokenRefreshDeferred: Deferred<string> | undefined

	axiosRetry(api, {
		retries: 5,

		retryDelay: (retryCount: number, error: AxiosError) => exponentialDelay(retryCount, error, 500),

		retryCondition: (error: AxiosError) => {
			const errorCode = isAxiosError(error) ? error.code : 'unknown'

			// A request can be retried if
			const canRetry =
				// it wasn't canceled
				'ERR_CANCELED' !== errorCode &&
				// and it was a 401 error in embedded mode
				((isUnauthorizedRequest(error) && isEmbedded.value) ||
					// or it was a network or retryable error (see https://github.com/softonic/axios-retry/blob/master/src/index.ts)
					((isNetworkError(error) || isRetryableError(error)) &&
						// and it was a non-POST request
						(isIdempotentRequestError(error) ||
							// or a POST request with special flag
							error.config?.fetchOptions?.canRetry)))

			if (!canRetry) {
				trackException(error, 'HTTP | Prevented failed request retry', getTelemetryEventPayload(error))
			}

			return canRetry
		},

		onRetry: async (retryCount, error, requestConfig) => {
			trackException(
				error,
				'HTTP | Retrying failed request',
				getTelemetryEventPayload(error, {
					retryAttemptCount: retryCount
				})
			)

			if (isUnauthorizedRequest(error)) {
				/* In case when we're retrying a request after a 401 response, it means that we're in embedded mode.
				In such case we init a promise which will be resolved once auth token is updated, request a refresh
				of the auth token and wait for the promise. Once the promise is resolved with new auth token we set
				that token in the retried request which is then retried */

				trackEvent('app', 'HTTP | Unauthorized request. Refreshing auth token.', getTelemetryEventPayload(error))

				authTokenRefreshDeferred = createDeferred<string>()

				emitToParent('GET_TOKEN')

				const newAuthToken = await authTokenRefreshDeferred.promise

				requestConfig.headers = {
					...requestConfig.headers,
					Authorization: `Bearer ${newAuthToken}`
				}

				trackEvent('app', 'HTTP | Unauthorized request. Token was refreshed.', getTelemetryEventPayload(error))
			}
		},

		onMaxRetryTimesExceeded: (error, retryCount) => {
			trackException(
				error,
				'HTTP | Exceeded max retry attempts',
				getTelemetryEventPayload(error, {
					retryAttemptCount: retryCount
				})
			)
		}
	})

	const updateHeaderAuthorization = (token: string) => {
		api.defaults.headers.common['Authorization'] = `Bearer ${token}`

		authTokenRefreshDeferred?.resolve?.(token)

		trackEvent('app', 'HTTP | Updated authorization token.')
	}

	const updateAppVersionHeader = (appVersion: Nullable<string> | undefined): void => {
		if (!appVersion) return

		api.defaults.headers.common['app-version'] = appVersion

		trackEvent('app', 'HTTP | Updated application version.', {
			data: appVersion
		})
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const addResponseInterceptor = (onFulfilled?: ((value: any) => any | Promise<any>) | null, onRejected?: (error: unknown) => unknown) => {
		return api.interceptors.response.use(onFulfilled, onRejected)
	}

	const removeResponseInterceptor = (id: number) => {
		if (Number.isSafeInteger(id)) {
			api.interceptors.response.eject(id)
		}
	}

	return {
		apiGet: api.get,
		apiPost: api.post,
		apiPut: api.put,
		apiDelete: api.delete,

		updateAppVersionHeader,
		updateHeaderAuthorization,
		addResponseInterceptor,
		removeResponseInterceptor
	}
}

export const useApi = createSharedComposable(createApi)
